import React,{useState} from "react";
import * as XLSX from "xlsx";
import BarcodeScannerModal from "./BarcodeScanner";
import SalesPages from "./SalesPages";
import MastersPages from "./MastersPages";
import OpsPages from "./OpsPages";
import ReportsPages from "./ReportsPages";
import AdminPages from "./AdminPages";

// Universal Filter Bar — defined ONCE at module scope (not inside PageContent's render)
// so React keeps the same component identity across re-renders. Defining this inside a
// render function recreates it on every keystroke, which makes React unmount+remount the
// <input> DOM node and the search box loses focus after every single character typed.
function UnivFilter({placeholder="🔍 Search...",srch,setSrch,dateFrom,setDateFrom,dateTo,setDateTo,companies,cf,setCf,C}){
  return <div style={{marginBottom:10}}>
    <input placeholder={placeholder} value={srch} onChange={e=>setSrch(e.target.value)}
      style={{width:"100%",padding:"8px 13px",borderRadius:9,border:`1.5px solid ${C.border}`,
      fontSize:13,background:"#fff",outline:"none",boxSizing:"border-box",marginBottom:8,fontFamily:"inherit"}}/>
    <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
      <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
        style={{padding:"5px 8px",borderRadius:6,border:`1.5px solid ${C.border}`,fontSize:11,fontFamily:"inherit"}}/>
      <span style={{fontSize:11,color:C.sub}}>to</span>
      <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
        style={{padding:"5px 8px",borderRadius:6,border:`1.5px solid ${C.border}`,fontSize:11,fontFamily:"inherit"}}/>
      {(dateFrom||dateTo||srch)&&<button onClick={()=>{setSrch("");setDateFrom("");setDateTo("");}}
        style={{padding:"4px 10px",borderRadius:6,border:"none",background:C.danger,color:"#fff",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"inherit"}}>✕ Clear</button>}
      {companies?.length>1&&<select value={cf} onChange={e=>setCf(e.target.value)}
        style={{padding:"5px 8px",borderRadius:6,border:`1.5px solid ${C.border}`,fontSize:11,fontFamily:"inherit",background:"#fff"}}>
        <option value="all">All Companies</option>
        {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
      </select>}
    </div>
  </div>;
}

// Compresses/resizes an uploaded photo before converting to base64 — real phone camera
// photos are often 3-8MB each, and two of them (photo + aadhar photo) together can
// exceed the server's request size limit, causing the whole "Add Staff" save to fail
// with a confusing error. Resizing to a reasonable max width fixes this and also
// keeps the database lighter.
function compressImage(file, maxWidth, quality, callback){
  const reader=new FileReader();
  reader.onload=(ev)=>{
    const img=new Image();
    img.onload=()=>{
      const scale=Math.min(1, maxWidth/img.width);
      const canvas=document.createElement("canvas");
      canvas.width=img.width*scale;
      canvas.height=img.height*scale;
      const ctx=canvas.getContext("2d");
      ctx.drawImage(img,0,0,canvas.width,canvas.height);
      callback(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror=()=>callback(ev.target.result); // fallback: use original if resize fails
    img.src=ev.target.result;
  };
  reader.readAsDataURL(file);
}

// Reads a CSV or XLSX file and returns an array of row objects (same shape either way)
function readBulkFile(file, onRows, onError){
  const ext=(file.name||"").toLowerCase().split(".").pop();
  if(ext==="xlsx"||ext==="xls"){
    const reader=new FileReader();
    reader.onload=ev=>{
      try{
        const wb=XLSX.read(ev.target.result,{type:"array"});
        const sheet=wb.Sheets[wb.SheetNames[0]];
        const rows=XLSX.utils.sheet_to_json(sheet,{defval:"",raw:true});
        onRows(rows.map(r=>{
          const obj={};
          Object.keys(r).forEach(k=>{
            let v=r[k];
            if(typeof v==="number") v=String(v); // real numeric cell — safe as-is, no separator issues
            else {
              v=String(v||"").trim();
              // If it LOOKS like a comma-formatted number (e.g. "1,200.50"), strip the commas
              if(/^[\d,]+(\.\d+)?$/.test(v)) v=v.replace(/,/g,"");
            }
            obj[String(k).trim()]=v;
          });
          return obj;
        }));
      }catch{ onError(); }
    };
    reader.readAsArrayBuffer(file);
  } else {
    const reader=new FileReader();
    reader.onload=ev=>{
      try{
        const lines=ev.target.result.split("\n").filter(l=>l.trim());
        const headers=lines[0].split(",").map(h=>h.trim().replace(/"/g,""));
        const rows=lines.slice(1).map(line=>{
          const vals=line.split(",").map(v=>v.trim().replace(/"/g,""));
          const obj={};headers.forEach((h,i)=>obj[h]=vals[i]||"");return obj;
        });
        onRows(rows);
      }catch{ onError(); }
    };
    reader.readAsText(file);
  }
}

export default function PageContent(props){
  const {
    page, nav, isA, isDB, user, lang, T, C, fmt, today, todayStr,
    monthStart, monthEnd, fyStart, fyEnd, last7, last30, filterByDate,
    getFYList, gCalc, dlCSV, numToWords,
    canAccess, getPlanLimit, tenantPlan, tenantInfo, pricing,
    companies, setCompanies, custs, setCusts, supps, setSupps,
    prods, setProds, orders, setOrders, pays, setPays,
    purs, setPurs, notes, setNotes, users, setUsers,
    beats, setBeats, expenses, setExpenses, cheques, setCheques,
    modal, setModal, form, setForm,
    payOid, setPayOid, nItems, setNItems,
    payAmt, setPayAmt, payMethod2, setPayMethod2,
    payingCust, setPayingCust, dbOtp, setDbOtp,
    dbOtpOrderId, setDbOtpOrderId,
    dateFrom, setDateFrom, dateTo, setDateTo,
    gTab, setGTab, selFY, setSelFY, cf, setCf, prev, setPrev,
    aiInsight, aiLoading, aiPurSug, setAiPurSug,
    myCompany, setMyCompany,
    fOrders, fPays, fPurs, lowS, tSales, tPaid, tDue,
    loadData, showToast, handleOrder, handlePur, addPay, setSubPage, subPage,
    getAIInsights, getSmartPurchaseSuggestions,
    sendDailyReport, sendPaymentReminder, gc, API,
    accounts, setAccounts,
    pInv, pInvBulk, pPur, pNote, pReceipt, waShare,
    Bdg, CB, Card, Sc, Row, Btn, Inp, Sel, Bar, Modal,
    AddProdToInvoice, InvModal, BankRecon, PhotoScanModal,
    NC, NI, SC, CC, editC, editComp, setEditComp
  } = props;

  // ── Local States ──────────────────────────────────────────
  const [srch,setSrch]=useState("");
  const [oTab,setOTab]=useState("active");
  const [oPage,setOPage]=useState(0);
  const [payForm,setPayForm]=useState({cust:"",amt:"",method:"Cash",note:"",account_id:""});

  // ── Constants ────────────────────────────────────────────
  const UNITS=["PCS","BOX","KG","LTR","ML","GM","SET","PAIR","DOZEN","PACK"];
  const [showScanner,setShowScanner]=useState(false); // barcode scanner modal — used in Product form + Order/Purchase entry

  // ── Helper Functions ─────────────────────────────────────
  const CFbar=({v,max,c})=>{
    const p=max>0?Math.min(100,Math.round(v/max*100)):0;
    return <div style={{background:C.border,borderRadius:5,height:6,overflow:"hidden"}}><div style={{background:c,height:"100%",width:`${p}%`,borderRadius:5}}/></div>;
  };

  const addNItem=(p)=>{
    if(p){
      // Add from product list - pre-fill name, price, hsn
      if(nItems.find(i=>i.pid===p.id)) return; // already added
      // Purchase Return → use purchase price (pp), Sale Return → use sale price (sp)
      const price=(form.nt==="Purchase Return") ? (p.pp||p.sp||0) : (p.sp||0);
      setNItems([...nItems,{pid:p.id,name:p.name,hsn:p.hsn||"33059090",qty:1,price}]);
    } else {
      setNItems([...nItems,{pid:Date.now(),name:"",hsn:"33059090",qty:1,price:0}]);
    }
  };

  const createNote=async()=>{
    if(!form.nt||!form.cid||!form.party)return showToast("Please fill required fields!","err");
    if((form.nt==="Sale Return"||form.nt==="Purchase Return")&&nItems.length===0)return showToast("Please add at least one item!","err");
    if((form.nt==="Credit Note"||form.nt==="Debit Note")&&!form.amt)return showToast("Please enter amount!","err");
    const co=companies.find(c=>c.id===form.cid);
    const n=(co?.ctr||0)+1;
    const pf=co?.postfix?`-${co.postfix}`:"";
    const no=`${co?.prefix||"NOTE"}${pf}-N${String(n).padStart(3,"0")}`;
    // Credit/Debit Note: use manual amount; Sale/Purchase Return: use items
    const hasItems=(form.nt==="Sale Return"||form.nt==="Purchase Return");
    const taxable=hasItems ? nItems.reduce((s,i)=>s+i.price*i.qty,0) : (+form.amt||0);
    const gRate=companies.find(c=>c.id===form.cid)?.gst||18;
    const g=gCalc(taxable,gRate,form.gt||"CGST_SGST");
    const noteData={
      id:"NOTE"+Date.now(),note_no:no,type:form.nt,cid:form.cid,
      party:form.party,phone:form.phone||"",ref_invoice:form.refInv||"",
      date:today(),reason:form.reason||"",gst_type:form.gt||"CGST_SGST",
      taxable,gst_amt:Math.round(g.t),total:Math.round(taxable+g.t),
      status:"Issued",items:hasItems?nItems:[]
    };
    setNotes([...notes,noteData]);
    setModal(null);setForm({});setNItems([]);
    try{await API.createNote(noteData);await loadData(true);}catch(e){}
    showToast(`✅ ${form.nt} ${no} created!`);
  };

  const [editMyComp, setEditMyComp] = useState(false);
  const [selRep, setSelRep] = useState("");

  // ── All props for sub-components ──────────────────────────────
  const p = {
    ...props,
    srch, setSrch, oTab, setOTab, oPage, setOPage,
    payForm, setPayForm,
    editMyComp, setEditMyComp,
    selRep, setSelRep,
    UnivFilter, CFbar,
  };

  return(
    <>
      <SalesPages {...p}/>
      <MastersPages {...p}/>
      <OpsPages {...p}/>
      <ReportsPages {...p}/>
      <AdminPages {...p}/>

      {modal==="note"&&<Modal title={`${NI[form.nt]||"📋"} ${form.nt||"Create Note"}`} oc={()=>{setModal(null);setForm({});setNItems([]);}} w ch={<div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7,marginBottom:10}}>
          {["Credit Note","Debit Note","Sale Return","Purchase Return"].map(t=>{const nc=NC[t];const sel=form.nt===t;return <button key={t} onClick={()=>setForm({...form,nt:t})} style={{padding:"9px 8px",borderRadius:9,border:`2px solid ${sel?nc:C.border}`,background:sel?nc+"15":"#fff",cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}><div style={{fontSize:14,marginBottom:2}}>{NI[t]}</div><div style={{fontSize:11,fontWeight:700,color:sel?nc:C.text}}>{t}</div></button>;})}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7}}>
          <Sel l="Company" v={form.cid||""} oc={e=>setForm({...form,cid:e.target.value})} req ch={[<option key="" value="">-- Select --</option>,...companies.filter(c=>c.active).map(c=><option key={c.id} value={c.id}>{c.name}</option>)]}/>
          <Sel l="GST Type" v={form.gt||"CGST_SGST"} oc={e=>setForm({...form,gt:e.target.value})} ch={[<option key="cs" value="CGST_SGST">CGST+SGST</option>,<option key="ig" value="IGST">IGST</option>]}/>
        </div>
        <Sel l={form.nt==="Purchase Return"?"Supplier":"Customer"} v={form.party||""} oc={e=>setForm({...form,party:e.target.value})} req ch={[<option key="" value="">-- Select --</option>,...(form.nt==="Purchase Return"?supps:custs).map(x=><option key={x.id} value={x.name}>{x.name}</option>)]}/>
        <Inp l="Ref Invoice / Purchase ID" v={form.refInv||""} oc={e=>setForm({...form,refInv:e.target.value})} p="Optional"/>
        <Inp l="Reason" v={form.reason||""} oc={e=>setForm({...form,reason:e.target.value})} p="e.g. Damaged goods, over-billing..."/>
        {(form.nt==="Sale Return"||form.nt==="Purchase Return")&&<>
          <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:5}}>Items (Stock auto adjust hoga):</div>
          <div style={{maxHeight:110,overflowY:"auto",marginBottom:7,background:C.bg,borderRadius:8,padding:6}}>
            {prods.filter(p=>!form.cid||p.cid===form.cid).map(p=><div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 8px",background:"#fff",borderRadius:6,marginBottom:3,border:`1px solid ${C.border}`}}>
              <div><div style={{fontSize:11,fontWeight:600}}>{p.name}</div><div style={{fontSize:9,color:C.sub}}>₹{form.nt==="Purchase Return"?p.pp:p.sp} • Stock:{p.stock}</div></div>
              <button onClick={()=>addNItem(p)} style={{background:NC[form.nt]||C.primary,border:"none",color:"#fff",width:22,height:22,borderRadius:5,cursor:"pointer",fontWeight:700}}>+</button>
            </div>)}
          </div>
          {nItems.length>0&&<div style={{background:C.bg,borderRadius:8,padding:7,marginBottom:7}}>
            {nItems.map(it=><div key={it.pid} style={{display:"flex",alignItems:"center",gap:5,marginBottom:3,background:"#fff",borderRadius:5,padding:"4px 7px",border:`1px solid ${C.border}`}}>
              <span style={{flex:1,fontSize:11,fontWeight:600}}>{it.name}</span>
              <button onClick={()=>setNItems(nItems.map(i=>i.pid===it.pid&&i.qty>1?{...i,qty:i.qty-1}:i))} style={{background:C.bg,border:`1px solid ${C.border}`,width:20,height:20,borderRadius:4,cursor:"pointer",fontWeight:700}}>-</button>
              <span style={{fontSize:12,fontWeight:700,color:NC[form.nt],minWidth:18,textAlign:"center"}}>{it.qty}</span>
              <button onClick={()=>setNItems(nItems.map(i=>i.pid===it.pid?{...i,qty:i.qty+1}:i))} style={{background:NC[form.nt]||C.primary,border:"none",color:"#fff",width:20,height:20,borderRadius:4,cursor:"pointer",fontWeight:700}}>+</button>
              <span style={{fontSize:10,color:C.sub}}>₹{fmt(it.qty*it.price)}</span>
              <button onClick={()=>setNItems(nItems.filter(i=>i.pid!==it.pid))} style={{background:C.danger,border:"none",color:"#fff",width:18,height:18,borderRadius:4,cursor:"pointer"}}>×</button>
            </div>)}
          </div>}
        </>}
        {(form.nt==="Credit Note"||form.nt==="Debit Note")&&<Inp l="Amount (Taxable) ₹" v={form.amt||""} oc={e=>setForm({...form,amt:e.target.value})} t="number" p="GST will be calculated separately"/>}
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}><Btn ch={`✅ Create ${form.nt||"Note"}`} oc={createNote} s={{flex:1}}/><Btn ch="Cancel" oc={()=>{setModal(null);setForm({});setNItems([]);}} color={C.sub} out s={{flex:1}}/></div>
      </div>}/>}

      {/* Product Modal */}
      {modal==="prod"&&<Modal title={form.id?`✏️ Edit Product — ${form.name||""}`:"🧴 Add Product"} oc={()=>setModal(null)} w ch={<div>
        <Sel l="Company" v={form.cid||""} oc={e=>setForm({...form,cid:e.target.value})} req ch={[<option key="" value="">-- Select --</option>,...companies.filter(c=>c.active).map(c=><option key={c.id} value={c.id}>{c.name}</option>)]}/>
        <Inp l="Product Name" v={form.name||""} oc={e=>setForm({...form,name:e.target.value})} req/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <Inp l="Brand" v={form.brand||""} oc={e=>setForm({...form,brand:e.target.value})}/>
          <Inp l="Category" v={form.cat||""} oc={e=>setForm({...form,cat:e.target.value})}/>
          <Inp l="HSN Code" v={form.hsn||""} oc={e=>setForm({...form,hsn:e.target.value})}/>
          <Sel l="GST Rate" v={form.gst||18} oc={e=>setForm({...form,gst:+e.target.value})} ch={["0","5","12","18","28"].map(r=><option key={r} value={r}>{r}%</option>)}/>
          <Inp l="MRP ₹" v={form.mrp||""} oc={e=>setForm({...form,mrp:+e.target.value})} t="number"/>
          <Inp l="Sale Price ₹" v={form.sp||""} oc={e=>setForm({...form,sp:+e.target.value})} t="number"/>
          <Inp l="Purchase Price ₹" v={form.pp||""} oc={e=>setForm({...form,pp:+e.target.value})} t="number"/>
          <Inp l="Opening Stock" v={form.stock||""} oc={e=>setForm({...form,stock:+e.target.value})} t="number"/>
          <Inp l="Min Stock (Alert)" v={form.min||""} oc={e=>setForm({...form,min:+e.target.value})} t="number"/>
          <Sel l="Unit / Packing" v={form.unit||"PCS"} oc={e=>setForm({...form,unit:e.target.value})} ch={UNITS.map(u=><option key={u} value={u}>{u}</option>)}/>
        </div>
        <div style={{background:"#FFFBEB",border:`1.5px solid #FDE68A`,borderRadius:9,padding:"10px 12px",marginBottom:10}}>
          <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
            <input type="checkbox" checked={!!form.gst_inclusive} onChange={e=>setForm({...form,gst_inclusive:e.target.checked})} style={{width:16,height:16}}/>
            <span style={{fontSize:12,fontWeight:600,color:"#92400E"}}>💰 Sale/Purchase Price already includes GST</span>
          </label>
          <div style={{fontSize:10,color:"#92400E",marginTop:5,paddingLeft:24}}>
            {form.gst_inclusive
              ? `✅ ON — Invoice won't add GST on top. Price entered (₹${form.sp||0}) is the final amount customer pays; GST of ${form.gst||18}% will be shown as already included in it.`
              : `OFF (default) — Invoice will ADD ${form.gst||18}% GST on top of the price entered.`}
          </div>
        </div>
        <div style={{marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>📷 Barcode (optional)</div>
          <div style={{display:"flex",gap:7}}>
            <input value={form.barcode||""} onChange={e=>setForm({...form,barcode:e.target.value})} placeholder="Scan or type barcode number..." style={{flex:1,padding:"8px 11px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:12,outline:"none",fontFamily:"inherit"}}/>
            <Btn ch="📷 Scan" oc={()=>setShowScanner(true)} color={C.info} sm/>
          </div>
        </div>
        {/* Scheme section removed — was never applied in order/invoice flow */}
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          <Btn ch="💾 Save Product" oc={async()=>{
            if(!form.name||!form.cid)return;
            const isEdit=!!form.id;
            const np={id:form.id||Date.now(),name:form.name,brand:form.brand||"-",cat:form.cat||"General",
              hsn:form.hsn||"33059090",mrp:form.mrp||0,sp:form.sp||0,pp:form.pp||0,
              stock:form.stock||0,gst:form.gst||18,min:form.min||10,cid:form.cid,
              unit:form.unit||"PCS",barcode:form.barcode||"",gst_inclusive:!!form.gst_inclusive
            };
            setModal(null);setForm({});
            try{
              if(isEdit){ await API.updateProduct(np); }
              else { await API.createProduct(np); }
              await loadData(true);
              showToast(isEdit?"✅ Product updated!":"✅ Product added!");
            }catch(e){showToast("❌ Failed: "+(e.message||"Server error"),"err");}
          }} s={{flex:1}}/>
          <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
        </div>
      </div>}/>}

      {/* Customer Modal */}
      {modal==="cust"&&<Modal title={form.id?`✏️ Edit Customer — ${form.name||""}`:"👥 Add Customer"} oc={()=>setModal(null)} w ch={<div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <Inp l="Customer / Shop Name" v={form.name||""} oc={e=>setForm({...form,name:e.target.value})} req/>
          <Inp l="Contact Person" v={form.contact||""} oc={e=>setForm({...form,contact:e.target.value})}/>
          <Inp l="Phone" v={form.phone||""} oc={e=>setForm({...form,phone:e.target.value})} req/>
          <Inp l="City" v={form.city||""} oc={e=>setForm({...form,city:e.target.value})}/>
          {isA&&<Inp l="GSTIN" v={form.gstin||""} oc={e=>setForm({...form,gstin:e.target.value})}/>}
          {isA&&<Inp l="💳 Credit Limit (₹)" v={form.credit_limit||""} oc={e=>setForm({...form,credit_limit:+e.target.value})} t="number" p="0 = no limit"/>}
          {isA&&<div style={{background:"#FEF9C3",borderRadius:9,padding:"11px",marginBottom:10,border:"1.5px solid #FDE68A"}}>
            <div style={{fontSize:12,fontWeight:700,color:"#92400E",marginBottom:4}}>💰 Balance (Kitna Due Hai)</div>
            <div style={{fontSize:10,color:"#92400E",marginBottom:6}}>Opening balance + Invoice amount yahan track hoga. Agar purana due hai toh yahan daalo.</div>
            <input type="number" value={form.bal||""} onChange={e=>setForm({...form,bal:+e.target.value})} placeholder="0" style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1.5px solid ${C.danger}`,fontSize:16,fontWeight:700,color:C.danger,outline:"none",boxSizing:"border-box"}}/>
          </div>}
        </div>
        <div style={{marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>🏠 Full Address</div>
          <textarea value={form.address||""} onChange={e=>setForm({...form,address:e.target.value})} placeholder="Gali, Mohalla, Area, City - Pincode..." rows={2} style={{width:"100%",padding:"8px 11px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:12,outline:"none",boxSizing:"border-box",fontFamily:"inherit",resize:"vertical"}}/>
        </div>
        <div style={{marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>📍 Location Pin (optional)</div>
          <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
            <input value={form.loc||""} onChange={e=>setForm({...form,loc:e.target.value})} placeholder="Google Maps link paste karo..." style={{flex:1,padding:"8px 11px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:12,outline:"none",fontFamily:"inherit"}}/>
            <Btn ch="📍 Live" oc={()=>{if(navigator.geolocation){navigator.geolocation.getCurrentPosition(pos=>{setForm({...form,loc:`https://maps.google.com/?q=${pos.coords.latitude},${pos.coords.longitude}`});showToast("📍 Location captured!");},()=>showToast("Location denied","err"));}}} color={C.success} sm/>
          </div>
        </div>
        {beats.length>0&&<Sel l="🗺️ Beat (Route)" v={form.beat||""} oc={e=>setForm({...form,beat:e.target.value})} ch={[<option key="" value="">-- No Beat --</option>,...beats.map(b=><option key={b.id} value={b.name}>{b.name} ({b.day})</option>)]}/>}
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          <Btn ch="💾 Save Customer" oc={async()=>{
            if(!form.name||!form.phone){
              showToast("❌ Please fill in Name and Phone (dono zaroori hain)","err");
              return;
            }
            const isEdit=!!form.id;
            const nc={id:form.id||Date.now(),name:form.name,contact:form.contact||"-",phone:form.phone,city:form.city||"-",gstin:form.gstin||"",address:form.address||"",bal:+form.bal||0,paid:form.paid||0,loc:form.loc||"",beat:form.beat||"",credit_limit:+form.credit_limit||0,by:form.by||user.username};
            setModal(null);setForm({});
            try{
              if(isEdit){ await API.updateCustomer?.(nc) || await API.saveCustomer(nc); }
              else { await API.createCustomer(nc); }
              await loadData(true);
              showToast(isEdit?"✅ Customer updated!":"✅ Customer added!");
            }catch(e){
              showToast("❌ Failed: "+(e.message||"Server error"),"err");
            }
          }} s={{flex:1}}/>
          <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
        </div>
      </div>}/>}

      {/* Customer Edit Modal */}
      {modal==="editCust"&&<Modal title={`✏️ Edit Customer — ${form.name}`} oc={()=>setModal(null)} w ch={<div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <Inp l="Salon/Shop Name" v={form.name||""} oc={e=>setForm({...form,name:e.target.value})} req/>
          <Inp l="Contact Person" v={form.contact||""} oc={e=>setForm({...form,contact:e.target.value})}/>
          <Inp l="Phone" v={form.phone||""} oc={e=>setForm({...form,phone:e.target.value})} req/>
          <Inp l="City" v={form.city||""} oc={e=>setForm({...form,city:e.target.value})}/>
          <Inp l="GSTIN" v={form.gstin||""} oc={e=>setForm({...form,gstin:e.target.value})}/>
        </div>
        <div style={{marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>🏠 Full Address</div>
          <textarea value={form.address||""} onChange={e=>setForm({...form,address:e.target.value})} placeholder="Gali, Mohalla, Area, City - Pincode..." rows={2} style={{width:"100%",padding:"8px 11px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:12,outline:"none",boxSizing:"border-box",fontFamily:"inherit",resize:"vertical"}}/>
        </div>
        <div style={{marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>📍 Location Pin</div>
          <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
            <input value={form.loc||""} onChange={e=>setForm({...form,loc:e.target.value})} placeholder="Google Maps link..." style={{flex:1,padding:"8px 11px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:12,outline:"none",fontFamily:"inherit"}}/>
            <Btn ch="📍 Live" oc={()=>{if(navigator.geolocation){navigator.geolocation.getCurrentPosition(pos=>{setForm({...form,loc:`https://maps.google.com/?q=${pos.coords.latitude},${pos.coords.longitude}`});showToast("📍 Done!");},()=>showToast("Denied","err"));}}} color={C.success} sm/>
          </div>
        </div>
        <div style={{background:"#FEF9C3",borderRadius:9,padding:"12px",marginBottom:12,border:`1.5px solid #FDE68A`}}>
          <div style={{fontSize:12,fontWeight:700,color:"#92400E",marginBottom:4}}>💰 Current Balance (Due Amount)</div>
          <div style={{fontSize:10,color:"#92400E",marginBottom:8}}>Yeh amount customer ne aapko dena hai. Invoice add hone par badhta hai, payment aane par ghatta hai.</div>
          <input type="number" value={form.bal||0} onChange={e=>setForm({...form,bal:+e.target.value})} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.danger}`,fontSize:18,fontWeight:800,color:C.danger,outline:"none",boxSizing:"border-box"}}/>
          <div style={{fontSize:10,color:C.sub,marginTop:5}}>Formula: Opening Balance + Orders - Payments = Current Balance</div>
        </div>
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          <Btn ch="💾 Save Changes" oc={async()=>{if(!form.name||!form.phone)return;setCusts(custs.map(c=>c.id===form.id?{...form}:c));setModal(null);setForm({});try{await API.saveCustomer(form);await loadData(true);}catch(e){console.log(e);}showToast("✅ Customer updated!");}} color={C.success} s={{flex:1}}/>
          <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
        </div>
      </div>}/>}

      {/* Supplier Add Modal */}
      {modal==="supp"&&<Modal title={form.id?`✏️ Edit Supplier — ${form.name||""}`:"🏭 Add Supplier"} oc={()=>setModal(null)} w ch={<div>
        <Sel l="Company" v={form.cid||""} oc={e=>setForm({...form,cid:e.target.value})} req ch={[<option key="" value="">-- Select --</option>,...companies.filter(c=>c.active).map(c=><option key={c.id} value={c.id}>{c.name}</option>)]}/>
        <Inp l="Supplier Name" v={form.name||""} oc={e=>setForm({...form,name:e.target.value})} req/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <Inp l="Phone" v={form.phone||""} oc={e=>setForm({...form,phone:e.target.value})}/>
          <Inp l="City" v={form.city||""} oc={e=>setForm({...form,city:e.target.value})}/>
          <Inp l="GSTIN" v={form.gstin||""} oc={e=>setForm({...form,gstin:e.target.value})}/>
          <Inp l="Opening Due Balance ₹" v={form.bal||""} oc={e=>setForm({...form,bal:+e.target.value})} t="number"/>
        </div>
        <div style={{marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>🏠 Full Address</div>
          <textarea value={form.address||""} onChange={e=>setForm({...form,address:e.target.value})} placeholder="Plot/Shop no, Area, City - Pincode..." rows={2} style={{width:"100%",padding:"8px 11px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:12,outline:"none",boxSizing:"border-box",fontFamily:"inherit",resize:"vertical"}}/>
        </div>
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          <Btn ch="💾 Save Supplier" oc={async()=>{
            if(!form.name||!form.cid)return;
            const isEdit=!!form.id;
            const ns={id:form.id||Date.now(),name:form.name,phone:form.phone||"",city:form.city||"-",gstin:form.gstin||"",address:form.address||"",cid:form.cid,bal:+form.bal||0,paid:form.paid||0};
            setModal(null);setForm({});
            try{
              if(isEdit){ await API.updateSupplier(ns); }
              else { await API.createSupplier(ns); }
              await loadData(true);
              showToast(isEdit?"✅ Supplier updated!":"✅ Supplier added!");
            }catch(e){showToast("❌ Failed: "+(e.message||"Server error"),"err");}
          }} s={{flex:1}}/>
          <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
        </div>
      </div>}/>}

      {/* Product Edit Modal */}
      {modal==="editProd"&&<Modal title={`✏️ Edit — ${form.name}`} oc={()=>setModal(null)} w ch={<div>
        <Sel l="Company" v={form.cid||""} oc={e=>setForm({...form,cid:e.target.value})} req ch={[<option key="" value="">-- Select --</option>,...companies.filter(c=>c.active).map(c=><option key={c.id} value={c.id}>{c.name}</option>)]}/>
        <Inp l="Product Name" v={form.name||""} oc={e=>setForm({...form,name:e.target.value})} req/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <Inp l="Brand" v={form.brand||""} oc={e=>setForm({...form,brand:e.target.value})}/>
          <Inp l="Category" v={form.cat||""} oc={e=>setForm({...form,cat:e.target.value})}/>
          <Inp l="HSN Code" v={form.hsn||""} oc={e=>setForm({...form,hsn:e.target.value})}/>
          <Sel l="GST Rate" v={form.gst||18} oc={e=>setForm({...form,gst:+e.target.value})} ch={["0","5","12","18","28"].map(r=><option key={r} value={r}>{r}%</option>)}/>
          <Inp l="MRP ₹" v={form.mrp||""} oc={e=>setForm({...form,mrp:+e.target.value})} t="number"/>
          <Inp l="Sale Price ₹" v={form.sp||""} oc={e=>setForm({...form,sp:+e.target.value})} t="number"/>
          <Inp l="Purchase Price ₹" v={form.pp||""} oc={e=>setForm({...form,pp:+e.target.value})} t="number"/>
          <Inp l="Stock" v={form.stock||""} oc={e=>setForm({...form,stock:+e.target.value})} t="number"/>
          <Inp l="Min Stock" v={form.min||""} oc={e=>setForm({...form,min:+e.target.value})} t="number"/>
          <Sel l="Unit / Packing" v={form.unit||"PCS"} oc={e=>setForm({...form,unit:e.target.value})} ch={UNITS.map(u=><option key={u} value={u}>{u}</option>)}/>
        </div>
        {/* Scheme */}
        <div style={{background:"#FEF9C3",borderRadius:10,padding:"12px",marginBottom:12,border:"1.5px solid #FDE68A"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
            <div style={{fontSize:12,fontWeight:700,color:"#92400E"}}>🎁 Scheme</div>
            <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}>
              <input type="checkbox" checked={form.schemeActive||false} onChange={e=>setForm({...form,schemeActive:e.target.checked})} style={{width:16,height:16}}/>
              <span style={{fontSize:12,fontWeight:600,color:"#92400E"}}>Scheme On</span>
            </label>
          </div>
          {form.schemeActive&&<div>
            <div style={{display:"flex",gap:7,marginBottom:8}}>
              {[{v:"BOGO",l:"🎁 Buy X Get Y"},{v:"DISC",l:"💯 Qty Discount"},{v:"FLAT",l:"🏷️ Flat Price"}].map(({v,l})=>(
                <button key={v} onClick={()=>setForm({...form,schemeType:v})} style={{flex:1,padding:"6px 4px",borderRadius:8,border:`2px solid ${form.schemeType===v?"#D97706":"#FDE68A"}`,background:form.schemeType===v?"#D97706":"#fff",color:form.schemeType===v?"#fff":"#92400E",cursor:"pointer",fontFamily:"inherit",fontSize:11,fontWeight:600}}>{l}</button>
              ))}
            </div>
            {form.schemeType==="BOGO"&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <Inp l="Buy Qty" v={form.buyQty||""} oc={e=>setForm({...form,buyQty:+e.target.value})} t="number"/>
              <Inp l="Free Qty" v={form.freeQty||""} oc={e=>setForm({...form,freeQty:+e.target.value})} t="number"/>
            </div>}
            {form.schemeType==="DISC"&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <Inp l="Min Qty" v={form.buyQty||""} oc={e=>setForm({...form,buyQty:+e.target.value})} t="number"/>
              <Inp l="Discount %" v={form.discPct||""} oc={e=>setForm({...form,discPct:+e.target.value})} t="number"/>
            </div>}
            {form.schemeType==="FLAT"&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <Inp l="Min Qty" v={form.buyQty||""} oc={e=>setForm({...form,buyQty:+e.target.value})} t="number"/>
              <Inp l="Special Price ₹" v={form.flatPrice||""} oc={e=>setForm({...form,flatPrice:+e.target.value})} t="number"/>
            </div>}
            <Inp l="Scheme Label" v={form.schemeLabel||""} oc={e=>setForm({...form,schemeLabel:e.target.value})} p="e.g. Buy 6 Get 1 Free"/>
          </div>}
        </div>
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          <Btn ch="💾 Save Changes" oc={async()=>{
            if(!form.name||!form.cid)return;
            const up={...form};
            setProds(prods.map(p=>p.id===form.id?{...p,...up}:p));
            setModal(null);setForm({});
            try{await API.saveProduct(up);await loadData(true);}catch(e){console.log(e);}
            showToast("✅ Product updated!");
          }} color={C.success} s={{flex:1}}/>
          <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
        </div>
      </div>}/>}

      {/* PURCHASE EDIT MODAL */}
      {modal==="editPur"&&<Modal title={`✏️ Edit Purchase — ${form.id}`} oc={async()=>setModal(null)} w ch={<div>
        <Sel l="Company" v={form.cid||""} oc={e=>setForm({...form,cid:e.target.value})} req ch={[<option key="" value="">-- Select --</option>,...companies.filter(c=>c.active).map(c=><option key={c.id} value={c.id}>{c.name}</option>)]}/>
        <Inp l="Supplier Name" v={form.supp||""} oc={e=>setForm({...form,supp:e.target.value})} req/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <div><div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>Purchase Date</div><input type="date" value={form.date||""} onChange={e=>setForm({...form,date:e.target.value})} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:13,boxSizing:"border-box",fontFamily:"inherit"}}/></div>
          <Inp l="Paid Amount" v={form.paid||0} oc={e=>setForm({...form,paid:+e.target.value||0})} type="number"/>
        </div>

        {/* Items Section */}
        <div style={{background:"#F8FAFC",borderRadius:10,padding:"10px 12px",marginBottom:12,marginTop:6}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:12,fontWeight:700,color:C.sub}}>📦 Items ({form.items?.length||0})</div>
          </div>
          {(form.items||[]).map((it,i)=><div key={i} style={{background:"#fff",borderRadius:8,padding:"8px 10px",marginBottom:6,border:`1px solid ${C.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
              <input value={it.name||""} onChange={e=>{const newItems=[...form.items];newItems[i]={...newItems[i],name:e.target.value};setForm({...form,items:newItems});}} placeholder="Product name" style={{flex:1,padding:"4px 7px",border:`1px solid ${C.border}`,borderRadius:5,fontSize:11,marginRight:6,fontFamily:"inherit"}}/>
              <button onClick={()=>{const newItems=form.items.filter((_,idx)=>idx!==i);setForm({...form,items:newItems});}} style={{background:"transparent",border:`1px solid ${C.danger}`,color:C.danger,padding:"3px 7px",borderRadius:5,cursor:"pointer",fontSize:10}}>🗑️</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:5}}>
              <div><div style={{fontSize:9,color:C.sub}}>HSN</div><input value={it.hsn||""} onChange={e=>{const newItems=[...form.items];newItems[i]={...newItems[i],hsn:e.target.value};setForm({...form,items:newItems});}} style={{width:"100%",padding:"4px 6px",border:`1px solid ${C.border}`,borderRadius:4,fontSize:10,boxSizing:"border-box",fontFamily:"inherit"}}/></div>
              <div><div style={{fontSize:9,color:C.sub}}>Qty</div><input type="number" value={it.qty||0} onChange={e=>{const newItems=[...form.items];newItems[i]={...newItems[i],qty:+e.target.value||0};setForm({...form,items:newItems});}} style={{width:"100%",padding:"4px 6px",border:`1px solid ${C.border}`,borderRadius:4,fontSize:10,boxSizing:"border-box",fontFamily:"inherit"}}/></div>
              <div><div style={{fontSize:9,color:C.sub}}>Cost</div><input type="number" value={it.cp||0} onChange={e=>{const newItems=[...form.items];newItems[i]={...newItems[i],cp:+e.target.value||0};setForm({...form,items:newItems});}} style={{width:"100%",padding:"4px 6px",border:`1px solid ${C.border}`,borderRadius:4,fontSize:10,boxSizing:"border-box",fontFamily:"inherit"}}/></div>
              <div><div style={{fontSize:9,color:C.sub}}>GST%</div><input type="number" value={it.gst||18} onChange={e=>{const newItems=[...form.items];newItems[i]={...newItems[i],gst:+e.target.value||0};setForm({...form,items:newItems});}} style={{width:"100%",padding:"4px 6px",border:`1px solid ${C.border}`,borderRadius:4,fontSize:10,boxSizing:"border-box",fontFamily:"inherit"}}/></div>
            </div>
            <div style={{fontSize:10,color:C.success,marginTop:4,textAlign:"right",fontWeight:600}}>Total: ₹{fmt((it.cp||0)*(it.qty||0))}</div>
          </div>)}

          {/* Add Product Dropdown */}
          <div style={{background:"#F0F9FF",borderRadius:8,padding:"8px 10px",marginTop:6,border:`1.5px dashed ${C.primary}`}}>
            <div style={{fontSize:11,fontWeight:700,color:C.primary,marginBottom:6}}>➕ Naya Item Add Karo</div>
            <select onChange={e=>{
              const pid=+e.target.value;
              if(!pid)return;
              const prod=prods.find(p=>p.id===pid);
              if(!prod)return;
              const newItem={pid:prod.id,name:prod.name,hsn:prod.hsn||"33059090",qty:1,cp:prod.pp||0,gst:prod.gst||18};
              setForm({...form,items:[...(form.items||[]),newItem]});
              e.target.value="";
            }} style={{width:"100%",padding:"6px 9px",borderRadius:6,border:`1.5px solid ${C.primary}`,fontSize:11,fontFamily:"inherit"}}>
              <option value="">-- Product select karo --</option>
              {prods.filter(p=>!form.cid||p.cid===form.cid).map(p=><option key={p.id} value={p.id}>{p.name} (Stock: {p.stock})</option>)}
            </select>
            <button onClick={()=>setForm({...form,items:[...(form.items||[]),{pid:0,name:"",hsn:"33059090",qty:1,cp:0,gst:18}]})} style={{width:"100%",marginTop:5,padding:"6px",background:"transparent",border:`1px dashed ${C.sub}`,color:C.sub,borderRadius:6,cursor:"pointer",fontSize:10,fontFamily:"inherit"}}>+ Manual Item (Custom Product)</button>
          </div>
        </div>

        {/* Total Summary */}
        {(()=>{
          const sub=(form.items||[]).reduce((s,i)=>s+(i.cp||0)*(i.qty||0),0);
          const gamt=Math.round((form.items||[]).reduce((s,i)=>s+(i.cp||0)*(i.qty||0)*(i.gst||18)/100,0));
          const total=sub+gamt;
          return <div style={{background:"linear-gradient(135deg,#1C2B3A,#2D4A63)",borderRadius:10,padding:"10px 13px",marginBottom:12,color:"#fff"}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}><span>Subtotal:</span><span>₹{fmt(sub)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:3}}><span>GST:</span><span>₹{fmt(gamt)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:800,paddingTop:4,borderTop:"1px solid rgba(255,255,255,0.2)"}}><span>Total:</span><span>₹{fmt(total)}</span></div>
          </div>;
        })()}

        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          <Btn ch="💾 Save Changes" oc={async()=>{
            if(!form.supp||!form.cid)return;
            const sub=(form.items||[]).reduce((s,i)=>s+(i.cp||0)*(i.qty||0),0);
            const gamt=Math.round((form.items||[]).reduce((s,i)=>s+(i.cp||0)*(i.qty||0)*(i.gst||18)/100,0));
            const total=sub+gamt;
            const updated={...form,sub,gamt,total};

            // Adjust stock — find old purchase, reverse stock, then add new stock
            const oldPur=purs.find(p=>p.id===form.id);
            if(oldPur){
              // Reverse old stock
              setProds(prods.map(p=>{
                const oldIt=oldPur.items?.find(i=>i.pid===p.id);
                const newIt=form.items?.find(i=>i.pid===p.id);
                let stock=p.stock;
                if(oldIt)stock=Math.max(0,stock-oldIt.qty);
                if(newIt)stock=stock+newIt.qty;
                return {...p,stock};
              }));
            }

            setPurs(purs.map(p=>p.id===form.id?updated:p));
            setModal(null);setForm({});
            try{
              await API.deletePurchase(form.id);
              await API.createPurchase(updated);
              await loadData(true);
              showToast("✅ Purchase updated!");
            }catch(e){
              console.log(e);
              showToast("✅ Purchase update!");
            }
          }} color={C.success} s={{flex:1}}/>
          <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
        </div>
      </div>}/>}

      {/* Supplier Edit Modal */}
      {modal==="editSupp"&&<Modal title={`✏️ Edit Supplier — ${form.name}`} oc={()=>setModal(null)} w ch={<div>
        <Sel l="Company" v={form.cid||""} oc={e=>setForm({...form,cid:e.target.value})} req ch={[<option key="" value="">-- Select --</option>,...companies.filter(c=>c.active).map(c=><option key={c.id} value={c.id}>{c.name}</option>)]}/>
        <Inp l="Supplier Name" v={form.name||""} oc={e=>setForm({...form,name:e.target.value})} req/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <Inp l="Phone" v={form.phone||""} oc={e=>setForm({...form,phone:e.target.value})}/>
          <Inp l="City" v={form.city||""} oc={e=>setForm({...form,city:e.target.value})}/>
          <Inp l="GSTIN" v={form.gstin||""} oc={e=>setForm({...form,gstin:e.target.value})}/>
        </div>
        <div style={{marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>🏠 Full Address</div>
          <textarea value={form.address||""} onChange={e=>setForm({...form,address:e.target.value})} placeholder="Plot/Shop no, Area, City - Pincode..." rows={2} style={{width:"100%",padding:"8px 11px",borderRadius:8,border:`1.5px solid ${C.border}`,fontSize:12,outline:"none",boxSizing:"border-box",fontFamily:"inherit",resize:"vertical"}}/>
        </div>
        <div style={{background:"#FEF9C3",borderRadius:9,padding:"12px",marginBottom:12,border:`1.5px solid #FDE68A`}}>
          <div style={{fontSize:12,fontWeight:700,color:"#92400E",marginBottom:8}}>💰 Balance Adjustment</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            <div>
              <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>Due Balance ₹ (Hamein dena hai)</div>
              <input type="number" value={form.bal||0} onChange={e=>setForm({...form,bal:+e.target.value})} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.danger}`,fontSize:15,fontWeight:700,color:C.danger,outline:"none",boxSizing:"border-box"}}/>
            </div>
            <div>
              <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>Total Paid ₹ (Ab tak)</div>
              <input type="number" value={form.paid||0} onChange={e=>setForm({...form,paid:+e.target.value})} style={{width:"100%",padding:"9px 12px",borderRadius:8,border:`1.5px solid ${C.success}`,fontSize:15,fontWeight:700,color:C.success,outline:"none",boxSizing:"border-box"}}/>
            </div>
          </div>
        </div>
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          <Btn ch="💾 Save Changes" oc={async()=>{if(!form.name||!form.cid)return;setSupps(supps.map(s=>s.id===form.id?{...form}:s));setModal(null);setForm({});try{await API.saveSupplier(form);await loadData(true);}catch(e){console.log(e);}showToast("✅ Supplier updated!");}} color={C.success} s={{flex:1}}/>
          <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
        </div>
      </div>}/>}

      {/* Beat Modal */}
      {modal==="beat"&&<Modal title={form.id?`✏️ Edit Beat — ${form.name||""}`:"🗺️ Add Beat"} oc={()=>setModal(null)} ch={<div>
        <Inp l="Beat Name *" v={form.name||""} oc={e=>setForm({...form,name:e.target.value})} p="Monday-Andheri..."/>
        <Sel l="Default Day" v={form.day||"Monday"} oc={e=>setForm({...form,day:e.target.value})} ch={["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map(d=><option key={d}>{d}</option>)}/>
        <div style={{fontSize:10,color:C.sub,marginTop:-6,marginBottom:10}}>Yeh sirf default hai — "Aaj Ka Dispatch" se kisi bhi din override kar sakte ho.</div>

        <div style={{marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:5}}>👥 Assign Staff (ek se zyada select kar sakte ho)</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6,background:"#F8FAFC",border:`1.5px solid ${C.border}`,borderRadius:9,padding:10}}>
            {users.filter(u=>u.role==="staff"||u.role==="delivery").length===0&&<div style={{fontSize:11,color:C.sub}}>Koi staff/delivery boy nahi mila — pehle Staff add karo.</div>}
            {users.filter(u=>u.role==="staff"||u.role==="delivery").map(u=>{
              const selected=(form.staff||[]).includes(u.name);
              return <button key={u.id} type="button" onClick={()=>{
                const curr=form.staff||[];
                const next=selected?curr.filter(n=>n!==u.name):[...curr,u.name];
                setForm({...form,staff:next});
              }} style={{padding:"6px 12px",borderRadius:20,border:`1.5px solid ${selected?C.primary:C.border}`,background:selected?C.primary:"#fff",color:selected?"#fff":C.text,cursor:"pointer",fontSize:12,fontWeight:600,fontFamily:"inherit"}}>
                {selected?"✓ ":""}{u.name}
              </button>;
            })}
          </div>
        </div>

        <Inp l="Areas (comma separated)" v={form.areas||""} oc={e=>setForm({...form,areas:e.target.value})} p="Andheri West, Andheri East..."/>
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          <Btn ch="💾 Save" oc={async()=>{
            if(!form.name)return;
            const isEdit=!!form.id;
            const beatData={...form,id:form.id||("BEAT"+Date.now()),customers:form.customers||[],staff:form.staff||[]};
            setModal(null);setForm({});
            try{
              if(isEdit){ await API.updateBeat(beatData); }
              else { await API.createBeat(beatData); }
              await loadData(true);
              showToast(isEdit?"✅ Beat updated!":"✅ Beat added!");
            }catch(e){showToast("❌ Failed: "+(e.message||"Server error"),"err");}
          }} s={{flex:1}}/>
          <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
        </div>
      </div>}/>}

      {/* Expense Modal */}
      {modal==="expense"&&<Modal title="💸 Add Expense" oc={()=>setModal(null)} ch={<div>
        <Inp l="Amount ₹ *" v={form.amount||""} oc={e=>setForm({...form,amount:e.target.value})} t="number" req/>
        <Sel l="Category *" v={form.category||""} oc={e=>setForm({...form,category:e.target.value})} ch={[<option key="" value="">-- Select --</option>,...["Petrol/Travel","Staff Salary","Office Rent","Electricity","Marketing","Other"].map(c=><option key={c}>{c}</option>)]}/>
        <Inp l="Date *" v={form.date||today()} oc={e=>setForm({...form,date:e.target.value})} t="date" req/>
        <Inp l="Note" v={form.note||""} oc={e=>setForm({...form,note:e.target.value})} p="Description..."/>
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          <Btn ch="💾 Save" oc={async()=>{
            if(!form.amount||!form.category)return;
            const isEdit=!!form.id;
            const exp={id:form.id||("EXP"+Date.now()),...form,by:form.by||user.username,date:(form.date||today()).slice(0,10),amount:+form.amount};
            setModal(null);setForm({});
            try{
              if(isEdit){ await API.updateExpense(exp); }
              else { await API.createExpense(exp); }
              await loadData(true);
              showToast(isEdit?"✅ Expense updated!":"✅ Expense added!");
            }catch(e){showToast("❌ Failed: "+(e.message||"Server error"),"err");}
          }} s={{flex:1}}/>
          <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
        </div>
      </div>}/>}

      {/* Cheque Modal */}
      {modal==="cheque"&&<Modal title="📝 Add Cheque" oc={()=>setModal(null)} ch={<div>
        <Sel l="Cheque Type" v={form.type||"received"} oc={e=>setForm({...form,type:e.target.value})}
          ch={[<option key="received" value="received">📥 Received (from Customer)</option>,<option key="issued" value="issued">📤 Issued (to Supplier)</option>]}/>
        <Inp l="Party Name *" v={form.party||""} oc={e=>setForm({...form,party:e.target.value})} req/>
        <Inp l="Amount ₹ *" v={form.amount||""} oc={e=>setForm({...form,amount:e.target.value})} t="number" req/>
        <Inp l="Cheque Number *" v={form.cheque_no||""} oc={e=>setForm({...form,cheque_no:e.target.value})} req/>
        <Inp l="Bank Name *" v={form.bank||""} oc={e=>setForm({...form,bank:e.target.value})} req/>
        <Inp l="Due Date *" v={form.due_date||""} oc={e=>setForm({...form,due_date:e.target.value})} t="date" req/>
        <Inp l="Note" v={form.note||""} oc={e=>setForm({...form,note:e.target.value})} p="Invoice ref..."/>
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          <Btn ch="💾 Save" oc={async()=>{
            if(!form.party||!form.amount||!form.cheque_no||!form.bank||!form.due_date){showToast("Please fill in all required fields!","err");return;}
            const fmtDate=d=>d?d.slice(0,10):"";
            const newCheque={type:form.type||"received",party:form.party,amount:+form.amount,cheque_no:form.cheque_no,bank:form.bank,date:todayStr(),due_date:fmtDate(form.due_date),note:form.note||"",status:"Pending",by:user.username};
            try{
              await API.createCheque(newCheque);
              setModal(null);setForm({});
              await loadData(true);
              showToast("✅ Cheque added!");
            }catch(e){
              console.error("Cheque add error:",e.message);
              showToast("Failed to add cheque. Please try again.","err");
            }
          }} s={{flex:1}}/>
          <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
        </div>
      </div>}/>}

      {/* Company Modal */}
      {modal==="comp"&&<Modal title="🏢 Company" oc={()=>setModal(null)} w ch={<div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <Inp l="Company Name *" v={form.name||""} oc={e=>setForm({...form,name:e.target.value})} req/>
          <Inp l="Short Name (3-4) *" v={form.short||""} oc={e=>setForm({...form,short:e.target.value.toUpperCase().slice(0,4)})}/>
          <Inp l="Invoice Prefix *" v={form.prefix||""} oc={e=>setForm({...form,prefix:e.target.value.toUpperCase().slice(0,6)})} req/>
          <Inp l="Postfix (26-27)" v={form.postfix||""} oc={e=>setForm({...form,postfix:e.target.value.slice(0,7)})} p="26-27"/>
          <Inp l="Series Start" v={form.series_start||""} oc={e=>setForm({...form,series_start:e.target.value.replace(/\D/g,"")})} p="1"/>
        </div>
        <div style={{marginBottom:9}}><div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>Color</div><div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{CC.map(col=><button key={col} onClick={()=>setForm({...form,color:col})} style={{width:28,height:28,borderRadius:"50%",background:col,border:form.color===col?"3px solid #1E293B":"2px solid transparent",cursor:"pointer"}}/>)}</div></div>
        {form.prefix&&<div style={{marginBottom:9,padding:"8px 12px",background:(form.color||C.primary)+"12",borderRadius:7,fontSize:12,color:form.color||C.primary,fontWeight:700}}>
          📋 Preview: {form.prefix}{form.postfix?`-${form.postfix}`:""}-{String(form.series_start||1).padStart(4,"0")} → {form.prefix}{form.postfix?`-${form.postfix}`:""}-{String((+(form.series_start||1))+1).padStart(4,"0")}...
        </div>}
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}><Btn ch="💾 Save" oc={async()=>{
          if(!form.name||!form.prefix)return;
          const isNew=!form.id;
          const co2={...form,id:form.id||("C"+Date.now()),ctr:+(form.series_start||1)-1,postfix:form.postfix||"",series_start:+(form.series_start||1)};
          setModal(null);setForm({});
          try{
            // Use explicit create/update — saveCompany's isNewCo check fails because
            // new companies already have a "C"+timestamp id set (not empty)
            if(isNew){ await API.createCompany(co2); }
            else { await API.updateCompany(co2); }
            const freshCos = await API.getCompanies();
            if(Array.isArray(freshCos) && freshCos.length){
              setCompanies(freshCos.map(c=>({...c,
                short:c.short_name||c.short||c.name?.slice(0,3),
                color:c.color||"#2563EB",
                ctr:c.invoice_counter||0,
                active:c.active!==0
              })));
              showToast("✅ Company saved!");
            } else {
              // Fallback: keep optimistic add if server returned empty
              if(isNew){setCompanies([...companies,co2]);}
              showToast("✅ Company saved!");
            }
          }catch(e){
            console.error("Company save error:",e);
            showToast("❌ Failed to save: "+(e.message||"Server error"),"err");
          }
        }} s={{flex:1}}/><Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/></div>
      </div>}/>}

      {/* User Modal */}
      {modal==="user"&&<Modal title="👤 Add Staff" oc={()=>setModal(null)} ch={<div>
        <Inp l="Full Name *" v={form.name||""} oc={e=>setForm({...form,name:e.target.value})} req/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <Inp l="Username *" v={form.username||""} oc={e=>setForm({...form,username:e.target.value})} req/>
          <Inp l="Password *" v={form.password||""} oc={e=>setForm({...form,password:e.target.value})} req/>
          <Inp l="Phone *" v={form.phone||""} oc={e=>setForm({...form,phone:e.target.value})} req/>
        </div>
        <Sel l="Role" v={form.role||"staff"} oc={e=>setForm({...form,role:e.target.value})} ch={[
          <option key="s" value="staff">👤 Staff (Limited)</option>,
          <option key="a" value="admin">👑 Admin (Full)</option>,
          <option key="d" value="delivery">🚚 Delivery Boy</option>
        ]}/>
        <div style={{background:form.role==="admin"?"#F0FDF4":form.role==="delivery"?"#FFF7ED":"#EFF6FF",borderRadius:8,padding:"8px 11px",marginBottom:10,fontSize:11}}>
          {form.role==="admin"&&<span style={{color:C.success}}>✅ Full Access — All features</span>}
          {form.role==="delivery"&&<><div style={{color:C.warning,fontWeight:700}}>🚚 Delivery Boy Access:</div><div style={{color:C.success,marginTop:3}}>✅ My Deliveries, OTP Confirm</div><div style={{color:C.danger,marginTop:2}}>❌ Orders, Payments, Products, Settings</div></>}
          {(!form.role||form.role==="staff")&&<><div style={{color:C.success}}>✅ Orders, Payment Collection, Invoice</div><div style={{color:C.danger,marginTop:2}}>❌ Products, GST, Companies, Settings, Staff</div></>}
        </div>

        {/* Company Assignment — for Staff and Delivery */}
        {form.role!=="admin"&&<div style={{marginBottom:10}}>
          <Sel l="🏢 Assign Company" v={form.company_id||""} oc={e=>setForm({...form,company_id:e.target.value})} ch={[
            <option key="" value="">-- All Companies (no restriction) --</option>,
            ...companies.filter(c=>c.active!==0).map(c=><option key={c.id} value={c.id}>{c.name}</option>)
          ]}/>
          <div style={{fontSize:10,color:C.sub,marginTop:3}}>
            {form.company_id
              ? `✅ This staff will only see and work with ${companies.find(c=>c.id===form.company_id)?.name||"selected company"} data`
              : "⚠️ No restriction — staff can see all companies data"}
          </div>
        </div>}

        {/* Delivery Boy Extra Fields */}
        {form.role==="delivery"&&<div style={{background:"#FFF7ED",borderRadius:10,padding:12,marginBottom:10,border:`1px solid ${C.warning}40`}}>
          <div style={{fontWeight:700,fontSize:12,color:C.warning,marginBottom:8}}>🚚 Delivery Boy Details (Mandatory)</div>
          <Inp l="Address *" v={form.address||""} oc={e=>setForm({...form,address:e.target.value})}/>
          <Inp l="Emergency Contact *" v={form.emergency||""} oc={e=>setForm({...form,emergency:e.target.value})}/>
          <Inp l="Aadhar Number *" v={form.aadhar||""} oc={e=>setForm({...form,aadhar:e.target.value.replace(/\D/g,"").slice(0,12)})} p="12 digit aadhar..."/>
          <Inp l="Driving License" v={form.license||""} oc={e=>setForm({...form,license:e.target.value})} p="Optional..."/>

          {/* Photo Upload */}
          <div style={{marginBottom:9}}>
            <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>Photo * (Selfie)</div>
            {form.photo?<div style={{position:"relative",display:"inline-block"}}>
              <img src={form.photo} alt="photo" style={{width:80,height:80,borderRadius:10,objectFit:"cover",border:`2px solid ${C.success}`}}/>
              <button onClick={()=>setForm({...form,photo:null})} style={{position:"absolute",top:-6,right:-6,background:C.danger,border:"none",color:"#fff",width:20,height:20,borderRadius:"50%",cursor:"pointer",fontSize:11}}>×</button>
            </div>:<label style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",border:`2px dashed ${C.warning}`,borderRadius:9,cursor:"pointer",fontSize:12,color:C.warning,fontWeight:600}}>
              📷 Photo Upload
              <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(f){compressImage(f,800,0.7,dataUrl=>setForm(prev=>({...prev,photo:dataUrl})));}}}/>
            </label>}
          </div>

          {/* Aadhar Photo */}
          <div style={{marginBottom:9}}>
            <div style={{fontSize:11,fontWeight:600,color:C.sub,marginBottom:4}}>Aadhar Card Photo *</div>
            {form.aadhar_photo?<div style={{position:"relative",display:"inline-block"}}>
              <img src={form.aadhar_photo} alt="aadhar" style={{width:120,height:75,borderRadius:8,objectFit:"cover",border:`2px solid ${C.success}`}}/>
              <button onClick={()=>setForm({...form,aadhar_photo:null})} style={{position:"absolute",top:-6,right:-6,background:C.danger,border:"none",color:"#fff",width:20,height:20,borderRadius:"50%",cursor:"pointer",fontSize:11}}>×</button>
            </div>:<label style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",border:`2px dashed ${C.warning}`,borderRadius:9,cursor:"pointer",fontSize:12,color:C.warning,fontWeight:600}}>
              📄 Aadhar Photo Upload
              <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(f){compressImage(f,1000,0.7,dataUrl=>setForm(prev=>({...prev,aadhar_photo:dataUrl})));}}}/>
            </label>}
          </div>
        </div>}

        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          <Btn ch="💾 Add Staff" oc={async()=>{
            if(!form.name||!form.username||!form.password||!form.phone){showToast("Please fill in all fields!","err");return;}
            if(form.role==="delivery"&&(!form.aadhar||!form.photo||!form.aadhar_photo)){showToast("Photo and Aadhar are mandatory for delivery staff!","err");return;}
            if(users.find(u=>u.username===form.username)){showToast("Username already exists!","err");return;}
            const nu={id:Date.now(),name:form.name,username:form.username,password:form.password,
              role:form.role||"staff",active:true,phone:form.phone||"",
              address:form.address||"",emergency:form.emergency||"",
              aadhar:form.aadhar||"",license:form.license||"",
              photo:form.photo||"",aadhar_photo:form.aadhar_photo||"",company_id:form.company_id||null};
            setUsers([...users,nu]);
            setModal(null);setForm({});
            try{
              await API.createUser(nu);
              showToast("✅ Staff member added!");
              await loadData(true);
            }catch(e){
              console.error("Staff add error:",e.message);
              setUsers(users);
              showToast("❌ "+(e.message||"Failed to add staff member. Please try again."),"err");
            }
          }} s={{flex:1}}/>
          <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
        </div>
      </div>}/>}

      {modal==="editUser"&&<Modal title="✏️ Edit Staff" oc={()=>{setModal(null);setForm({});}} ch={<div>
        <Inp l="Full Name *" v={form.name||""} oc={e=>setForm({...form,name:e.target.value})} req/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
          <Inp l="Username *" v={form.username||""} oc={e=>setForm({...form,username:e.target.value})} req/>
          <Inp l="Phone *" v={form.phone||""} oc={e=>setForm({...form,phone:e.target.value})} req/>
        </div>
        <Sel l="Role" v={form.role||"staff"} oc={e=>setForm({...form,role:e.target.value})} ch={[
          <option key="s" value="staff">👤 Staff (Limited)</option>,
          <option key="a" value="admin">👑 Admin (Full)</option>,
          <option key="d" value="delivery">🚚 Delivery Boy</option>
        ]}/>
        <Sel l="Status" v={form.active===false?"inactive":"active"} oc={e=>setForm({...form,active:e.target.value==="active"})} ch={[
          <option key="act" value="active">✅ Active</option>,
          <option key="ina" value="inactive">❌ Inactive</option>
        ]}/>

        {/* Company Assignment */}
        {form.role!=="admin"&&<div style={{marginBottom:10}}>
          <Sel l="🏢 Assign Company" v={form.company_id||""} oc={e=>setForm({...form,company_id:e.target.value})} ch={[
            <option key="" value="">-- All Companies (no restriction) --</option>,
            ...companies.filter(c=>c.active!==0).map(c=><option key={c.id} value={c.id}>{c.name}</option>)
          ]}/>
          <div style={{fontSize:10,color:C.sub,marginTop:3}}>
            {form.company_id
              ? `✅ Only ${companies.find(c=>c.id===form.company_id)?.name||"selected company"} data visible`
              : "⚠️ No restriction — all companies data visible"}
          </div>
        </div>}

        {form.role==="delivery"&&<div style={{background:"#FFF7ED",borderRadius:10,padding:12,marginBottom:8,border:`1px solid ${C.warning}40`}}>
          <div style={{fontWeight:700,fontSize:12,color:C.warning,marginBottom:8}}>🚚 Delivery Details</div>
          <Inp l="Address" v={form.address||""} oc={e=>setForm({...form,address:e.target.value})}/>
          <Inp l="Emergency Contact" v={form.emergency||""} oc={e=>setForm({...form,emergency:e.target.value})}/>
          <Inp l="Aadhar Number" v={form.aadhar||""} oc={e=>setForm({...form,aadhar:e.target.value.replace(/\D/g,"").slice(0,12)})} p="12 digit aadhar..."/>
          <Inp l="Driving License" v={form.license||""} oc={e=>setForm({...form,license:e.target.value})} p="Optional..."/>
        </div>}
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
          <Btn ch="💾 Save Changes" oc={async()=>{
            if(!form.name||!form.username||!form.phone){showToast("Please fill in all required fields!","err");return;}
            if(users.find(u=>u.username===form.username&&u.id!==form.id)){showToast("Username already taken!","err");return;}
            try{
              await API.put("/users/"+form.id, form);
              setModal(null);setForm({});
              await loadData(true);
              showToast("✅ Staff updated!");
            }catch(e){
              showToast("Failed to update: "+e.message,"err");
            }
          }} s={{flex:1}}/>
          <Btn ch="Cancel" oc={()=>{setModal(null);setForm({});}} color={C.sub} out s={{flex:1}}/>
        </div>
      </div>}/>}

      {modal==="photoScan"&&<PhotoScanModal
        companies={companies} suppliers={supps} products={prods}
        onSave={pd=>{const id="PUR"+String(purs.length+1).padStart(3,"0");setPurs([...purs,{...pd,id,date:today(),status:"Received",by:user.username}]);pd.items.forEach(it=>setProds(prods.map(p=>p.id===it.pid?{...p,stock:p.stock+it.qty}:p)));setSupps(supps.map(s=>s.name===pd.supp?{...s,bal:(s.bal||0)+(pd.total-(pd.paid||0))}:s));setModal(null);showToast("✅ Purchase scan se add!");}}
        onClose={()=>setModal(null)}
      />}

      {modal==="bulkProducts"&&<Modal title="🧴 Bulk Products Upload (CSV/Excel)" oc={()=>setModal(null)} w ch={<div>
        <div style={{background:"#EFF6FF",borderRadius:9,padding:"10px",marginBottom:10,fontSize:11}}>
          <div style={{fontWeight:700,color:C.primary,marginBottom:5}}>📋 CSV Columns:</div>
          <code style={{fontSize:10,background:"#fff",padding:"5px 8px",borderRadius:6,display:"block",fontFamily:"monospace"}}>name, brand, category, hsn, mrp, sale_price, purchase_price, stock, min_stock, gst_rate, unit, company_id</code>
          <button onClick={()=>{
            const sampleCo1=companies[0]?.name||"ABC Company";
            const sampleCo2=companies[1]?.name||companies[0]?.name||"Ram Company";
            dlCSV("products_template.csv",["name","brand","category","hsn","mrp","sale_price","purchase_price","stock","min_stock","gst_rate","unit","barcode","company_id"],[["Kerastase Shampoo 300ml","Kerastase","Hair Care","33051000","1500","1200","900","50","10","18","Bottle","8901234567890",sampleCo1],["Wella Color Touch","Wella","Hair Color","33059090","1100","850","600","100","20","18","Box","",sampleCo2]]);
          }} style={{marginTop:7,padding:"5px 12px",background:C.success,border:"none",borderRadius:6,color:"#fff",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"inherit"}}>⬇️ Template Download</button>
          <div style={{fontSize:10,color:"#92400E",marginTop:6}}>💡 In the <b>company_id</b> column, type the exact company name (e.g. "{companies[0]?.name||"ABC"}") — not an ID code.</div>
        </div>
        <input type="file" accept=".csv,.xlsx,.xls" onChange={e=>{const file=e.target.files[0];if(!file)return;readBulkFile(file,rows=>{setForm({...form,bPreview:rows.filter(r=>r.name),bType:"products"});},()=>showToast("Invalid file format!","err"));}} style={{width:"100%",padding:"10px",border:`2px dashed ${C.border}`,borderRadius:9,fontSize:12,cursor:"pointer",marginBottom:10}}/>
        {form.bPreview&&form.bType==="products"&&<div>
          <div style={{fontSize:11,fontWeight:700,color:C.success,marginBottom:7}}>✅ {form.bPreview.length} products ready:</div>
          <div style={{maxHeight:160,overflowY:"auto",border:`1px solid ${C.border}`,borderRadius:8,marginBottom:10}}>
            <div style={{overflowX:"auto"}}><table style={{width:"100%",minWidth:500,borderCollapse:"collapse",fontSize:11}}>
              <thead><tr style={{background:C.primary}}><th style={{padding:"5px 6px",color:"#fff",textAlign:"left"}}>Name</th><th style={{padding:"5px 6px",color:"#fff"}}>Company</th><th style={{padding:"5px 6px",color:"#fff"}}>Sale₹</th><th style={{padding:"5px 6px",color:"#fff"}}>Stock</th></tr></thead>
              <tbody>{form.bPreview.slice(0,20).map((p,i)=>{
                const v=String(p.company_id||"").trim().toLowerCase();
                const matched=companies.find(c=>(c.name||"").toLowerCase()===v||(c.short||"").toLowerCase()===v||String(c.id).toLowerCase()===v);
                return <tr key={i} style={{background:i%2===0?"#F8FAFC":"#fff"}}><td style={{padding:"4px 6px"}}>{p.name}</td><td style={{padding:"4px 6px",color:matched?C.success:C.danger,fontWeight:matched?400:700}}>{matched?matched.name:`⚠️ "${p.company_id}" not found`}</td><td style={{padding:"4px 6px",textAlign:"right"}}>₹{p.sale_price}</td><td style={{padding:"4px 6px",textAlign:"right"}}>{p.stock}</td></tr>;
              })}</tbody>
            </table></div>
          </div>
          <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
            <Btn ch={`✅ Import ${form.bPreview.length} Products`} oc={async()=>{
              // Match company by name first (user-friendly: "ABC", "Kerastase" etc), then by exact ID, then fallback
              const findCid=(val)=>{
                if(!val) return companies[0]?.id||"";
                const v=String(val).trim().toLowerCase();
                const byName=companies.find(c=>(c.name||"").toLowerCase()===v||(c.short||"").toLowerCase()===v);
                if(byName) return byName.id;
                const byId=companies.find(c=>String(c.id).toLowerCase()===v);
                if(byId) return byId.id;
                return companies[0]?.id||"";
              };
              const unmatchedCos=new Set();
              const np=form.bPreview.map((p,i)=>{
                const matchedCid=findCid(p.company_id);
                if(p.company_id && matchedCid===companies[0]?.id && (companies[0]?.name||"").toLowerCase()!==String(p.company_id).trim().toLowerCase()){
                  unmatchedCos.add(p.company_id);
                }
                return {id:"BP"+Date.now()+i,name:p.name,brand:p.brand||"-",cat:p.category||"General",hsn:p.hsn||"33059090",mrp:+p.mrp||0,sp:+p.sale_price||0,pp:+p.purchase_price||0,stock:+p.stock||0,min:+p.min_stock||10,gst:+p.gst_rate||18,unit:p.unit||"PCS",barcode:p.barcode||"",cid:matchedCid};
              });
              if(unmatchedCos.size>0){
                showToast(`⚠️ Company not found: "${[...unmatchedCos].join('", "')}" — assigned to ${companies[0]?.name||"first company"} instead. Use exact company name from your Companies page.`,"err");
              }
              setProds([...prods,...np]);
              setModal(null);setForm({});
              let saved=0,failed=0;
              for(const p of np){try{await API.saveProduct(p);saved++;}catch(e){failed++;console.error("Product save failed:",p.name,e.message);}}
              await loadData(true);
              showToast(`✅ ${saved}/${np.length} products saved to the database!`);
            }} color={C.success} s={{flex:1}}/>
            <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
          </div>
        </div>}
        {!form.bPreview&&<Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{width:"100%"}}/>}
      </div>}/>}

      {modal==="bulkCustomers"&&<Modal title="👥 Bulk Customers Upload (CSV/Excel)" oc={()=>setModal(null)} w ch={<div>
        <div style={{background:"#EFF6FF",borderRadius:9,padding:"10px",marginBottom:10,fontSize:11}}>
          <div style={{fontWeight:700,color:C.primary,marginBottom:5}}>📋 CSV Columns:</div>
          <code style={{fontSize:10,background:"#fff",padding:"5px 8px",borderRadius:6,display:"block",fontFamily:"monospace"}}>name, contact, phone, city, gstin, address, balance</code>
          <button onClick={()=>dlCSV("customers_template.csv",["name","contact","phone","city","gstin","address","balance"],[["Glamour Salon","Priya","9876543210","Mumbai","27GSTIN001","Shop 1 Linking Road Mumbai","5000"],["Style Studio","Rahul","9765432109","Delhi","","B-24 Lajpat Nagar Delhi","0"]])} style={{marginTop:7,padding:"5px 12px",background:C.success,border:"none",borderRadius:6,color:"#fff",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"inherit"}}>⬇️ Template Download</button>
        </div>
        <input type="file" accept=".csv,.xlsx,.xls" onChange={e=>{const file=e.target.files[0];if(!file)return;readBulkFile(file,rows=>{setForm({...form,bPreview:rows.filter(r=>r.name&&r.phone),bType:"customers"});},()=>showToast("Invalid file format!","err"));}} style={{width:"100%",padding:"10px",border:`2px dashed ${C.border}`,borderRadius:9,fontSize:12,cursor:"pointer",marginBottom:10}}/>
        {form.bPreview&&form.bType==="customers"&&<div>
          <div style={{fontSize:11,fontWeight:700,color:C.success,marginBottom:7}}>✅ {form.bPreview.length} customers ready:</div>
          <div style={{maxHeight:160,overflowY:"auto",border:`1px solid ${C.border}`,borderRadius:8,marginBottom:10}}>
            <div style={{overflowX:"auto"}}><table style={{width:"100%",minWidth:500,borderCollapse:"collapse",fontSize:11}}>
              <thead><tr style={{background:C.success}}><th style={{padding:"5px 6px",color:"#fff",textAlign:"left"}}>Name</th><th style={{padding:"5px 6px",color:"#fff"}}>Phone</th><th style={{padding:"5px 6px",color:"#fff"}}>City</th><th style={{padding:"5px 6px",color:"#fff"}}>Balance</th></tr></thead>
              <tbody>{form.bPreview.slice(0,20).map((c,i)=><tr key={i} style={{background:i%2===0?"#F8FAFC":"#fff"}}><td style={{padding:"4px 6px",fontWeight:600}}>{c.name}</td><td style={{padding:"4px 6px",color:C.sub}}>{c.phone}</td><td style={{padding:"4px 6px"}}>{c.city}</td><td style={{padding:"4px 6px",textAlign:"right",color:+c.balance>0?C.danger:C.sub}}>₹{c.balance||0}</td></tr>)}</tbody>
            </table></div>
          </div>
          <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
            <Btn ch={`✅ Import ${form.bPreview.length} Customers`} oc={async()=>{
              const nc=form.bPreview.map((c,i)=>({id:"BC"+Date.now()+i,name:c.name,contact:c.contact||"-",phone:c.phone,city:c.city||"-",gstin:c.gstin||"",address:c.address||"",bal:+c.balance||0,paid:0,loc:"",by:user.username}));
              setCusts([...custs,...nc]);
              setModal(null);setForm({});
              let saved=0,failed=0;
              for(const c of nc){
                try{await API.createCustomer(c);saved++;}
                catch(e){failed++;console.error("Customer save failed:",c.name,e.message);}
              }
              await loadData(true);
              showToast(failed>0?`⚠️ ${saved} saved, ${failed} failed — check console`:`✅ ${saved} customers imported!`);
              await loadData(true);
              showToast(`✅ ${saved}/${nc.length} customers saved to the database!`);
            }} color={C.success} s={{flex:1}}/>
            <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
          </div>
        </div>}
        {!form.bPreview&&<Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{width:"100%"}}/>}
      </div>}/>}

      {modal==="bulkSuppliers"&&<Modal title="🏭 Bulk Suppliers Upload (CSV/Excel)" oc={()=>setModal(null)} w ch={<div>
        <div style={{background:"#EFF6FF",borderRadius:9,padding:"10px",marginBottom:10,fontSize:11}}>
          <div style={{fontWeight:700,color:C.primary,marginBottom:5}}>📋 CSV Columns:</div>
          <code style={{fontSize:10,background:"#fff",padding:"5px 8px",borderRadius:6,display:"block",fontFamily:"monospace"}}>name, phone, city, gstin, address, balance, company_id</code>
          <button onClick={()=>dlCSV("suppliers_template.csv",["name","phone","city","gstin","address","balance","company_id"],[["Kerastase Distributors","9876543210","Mumbai","27GSTIN002","Plot 5 Andheri Mumbai","12000","C1"],["Wella Traders","9765432109","Delhi","","A-10 Karol Bagh Delhi","0","C2"]])} style={{marginTop:7,padding:"5px 12px",background:C.success,border:"none",borderRadius:6,color:"#fff",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"inherit"}}>⬇️ Template Download</button>
        </div>
        <input type="file" accept=".csv,.xlsx,.xls" onChange={e=>{const file=e.target.files[0];if(!file)return;readBulkFile(file,rows=>{setForm({...form,bPreview:rows.filter(r=>r.name),bType:"suppliers"});},()=>showToast("Invalid file format!","err"));}} style={{width:"100%",padding:"10px",border:`2px dashed ${C.border}`,borderRadius:9,fontSize:12,cursor:"pointer",marginBottom:10}}/>
        {form.bPreview&&form.bType==="suppliers"&&<div>
          <div style={{fontSize:11,fontWeight:700,color:C.success,marginBottom:7}}>✅ {form.bPreview.length} suppliers ready:</div>
          <div style={{maxHeight:160,overflowY:"auto",border:`1px solid ${C.border}`,borderRadius:8,marginBottom:10}}>
            <div style={{overflowX:"auto"}}><table style={{width:"100%",minWidth:500,borderCollapse:"collapse",fontSize:11}}>
              <thead><tr style={{background:C.success}}><th style={{padding:"5px 6px",color:"#fff",textAlign:"left"}}>Name</th><th style={{padding:"5px 6px",color:"#fff"}}>Phone</th><th style={{padding:"5px 6px",color:"#fff"}}>City</th><th style={{padding:"5px 6px",color:"#fff"}}>Balance</th></tr></thead>
              <tbody>{form.bPreview.slice(0,20).map((s,i)=><tr key={i} style={{background:i%2===0?"#F8FAFC":"#fff"}}><td style={{padding:"4px 6px",fontWeight:600}}>{s.name}</td><td style={{padding:"4px 6px",color:C.sub}}>{s.phone}</td><td style={{padding:"4px 6px"}}>{s.city}</td><td style={{padding:"4px 6px",textAlign:"right",color:+s.balance>0?C.danger:C.sub}}>₹{s.balance||0}</td></tr>)}</tbody>
            </table></div>
          </div>
          <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
            <Btn ch={`✅ Import ${form.bPreview.length} Suppliers`} oc={async()=>{
              const ns=form.bPreview.map((s,i)=>({id:"BS"+Date.now()+i,name:s.name,phone:s.phone||"",city:s.city||"-",gstin:s.gstin||"",address:s.address||"",cid:s.company_id||companies[0]?.id||"",bal:+s.balance||0,paid:0}));
              setSupps([...supps,...ns]);
              setModal(null);setForm({});
              let saved=0,failed=0;
              for(const s of ns){
                try{await API.createSupplier(s);saved++;}
                catch(e){failed++;console.error("Supplier save failed:",s.name,e.message);}
              }
              await loadData(true);
              showToast(failed>0?`⚠️ ${saved} saved, ${failed} failed`:`✅ ${saved} suppliers imported!`);
              await loadData(true);
              showToast(`✅ ${saved}/${ns.length} suppliers saved to the database!`);
            }} color={C.success} s={{flex:1}}/>
            <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
          </div>
        </div>}
        {!form.bPreview&&<Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{width:"100%"}}/>}
      </div>}/>}


      {modal==="bulkOrders"&&<Modal title="📦 Bulk Orders Upload (CSV/Excel)" oc={()=>setModal(null)} w ch={<div>
        <div style={{background:"#FEF9C3",borderRadius:9,padding:"10px",marginBottom:10,fontSize:11}}>
          <div style={{fontWeight:700,color:"#92400E",marginBottom:5}}>📋 CSV Columns:</div>
          <code style={{fontSize:10,background:"#fff",padding:"5px 8px",borderRadius:6,display:"block",fontFamily:"monospace"}}>customer, phone, company_id, date, product_name, qty, price, gst_type</code>
          <button onClick={()=>dlCSV("orders_template.csv",["customer","phone","company_id","date","product_name","qty","price","gst_type"],[["Glamour Salon","9876543210","C1","2026-05-12","Kerastase Shampoo","3","1200","CGST_SGST"],["Style Studio","9765432109","C2","2026-05-12","Wella Color","4","850","IGST"]])} style={{marginTop:7,padding:"5px 12px",background:C.warning,border:"none",borderRadius:6,color:"#fff",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"inherit"}}>⬇️ Template Download</button>
        </div>
        <input type="file" accept=".csv,.xlsx,.xls" onChange={e=>{const file=e.target.files[0];if(!file)return;readBulkFile(file,rawRows=>{const rows=rawRows.filter(r=>r.customer&&r.product_name);const grouped={};rows.forEach(r=>{const key=r.customer+"_"+r.date+"_"+r.company_id;if(!grouped[key])grouped[key]={customer:r.customer,phone:r.phone,cid:r.company_id,date:r.date,gt:r.gst_type||"CGST_SGST",items:[]};grouped[key].items.push({name:r.product_name,qty:+r.qty||1,price:+r.price||0,disc:0,dp:0,hsn:"33059090",mrp:0});});setForm({...form,bPreview:Object.values(grouped),bType:"orders"});},()=>showToast("Invalid file format!","err"));}} style={{width:"100%",padding:"10px",border:`2px dashed ${C.border}`,borderRadius:9,fontSize:12,cursor:"pointer",marginBottom:10}}/>
        {form.bPreview&&form.bType==="orders"&&<div>
          <div style={{fontSize:11,fontWeight:700,color:C.success,marginBottom:7}}>✅ {form.bPreview.length} orders ready:</div>
          <div style={{maxHeight:150,overflowY:"auto",border:`1px solid ${C.border}`,borderRadius:8,marginBottom:10}}>
            {form.bPreview.map((o,i)=>{const co=gc(o.cid);const total=o.items.reduce((s,it)=>s+it.qty*it.price,0);return <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"7px 10px",borderBottom:`1px solid ${C.border}`,background:i%2===0?"#F8FAFC":"#fff",fontSize:11}}><div><b>{o.customer}</b><span style={{color:C.sub,marginLeft:6}}>{o.date}</span><div style={{color:C.sub,fontSize:10}}>{o.items.length} items</div></div><div style={{fontWeight:700,color:C.primary}}>₹{fmt(total)}</div></div>;})}
          </div>
          <div style={{display:"flex",gap:7,flexWrap:"wrap"}}>
            <Btn ch={`✅ Import ${form.bPreview.length} Orders`} oc={async()=>{
              // Track per-company invoice counters so multi-company bulk imports number correctly
              const coCounters={};
              const no=form.bPreview.map((o,idx)=>{
                const co=gc(o.cid);
                const itot=o.items.reduce((s,it)=>s+it.qty*it.price,0);
                const gamt=o.items.reduce((s,it)=>s+(it.qty*it.price*(it.gst||co?.gst||18)/100),0);
                const id="ORD"+Date.now()+idx;
                if(coCounters[o.cid]===undefined) coCounters[o.cid]=co?.invoice_counter||co?.ctr||0;
                coCounters[o.cid]+=1;
                const inv=(co?.prefix||"INV")+"-"+String(coCounters[o.cid]).padStart(4,"0");
                return{id,inv,cid:o.cid,cust:o.customer,phone:o.phone||"",cgstin:"",date:o.date||today(),items:o.items,sub:itot,odp:0,odisc:0,tax:itot,gamt,total:itot+gamt,paid:0,status:"Pending",gt:o.gt||"CGST_SGST",by:user.username};
              });
              setOrders([...orders,...no]);
              setModal(null);setForm({});
              let saved=0,failed=0;
              for(const o of no){try{await API.createOrder(o);saved++;}catch(e){failed++;console.error("Order save failed:",o.inv,e.message);}}
              if(failed>0) showToast(`⚠️ ${saved} saved, ${failed} failed!`,"err");
              await loadData(true);
              showToast(`✅ ${saved}/${no.length} orders saved to the database!`);
            }} color={C.warning} s={{flex:1}}/>
            <Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{flex:1}}/>
          </div>
        </div>}
        {!form.bPreview&&<Btn ch="Cancel" oc={()=>setModal(null)} color={C.sub} out s={{width:"100%"}}/>}
      </div>}/>}

      {/* Password Change Modal */}
      {/* ── PAYMENT MODAL ── */}
      {modal==="pay"&&<Modal title="💰 Payment Record Karo" oc={()=>{setModal(null);setForm({});setPayOid("");}} ch={<div>
        <Sel l="Customer *" v={form.cust||""} oc={e=>setForm({...form,cust:e.target.value})}
          ch={[<option key="" value="">-- Customer Select --</option>,...custs.slice().sort((a,b)=>a.name.localeCompare(b.name)).map(c=><option key={c.id} value={c.name}>{c.name}{(c.bal||0)>0?` (Due: ₹${fmt(c.bal)})`:"" }</option>)]}/>
        {payOid&&<div style={{fontSize:11,color:C.info,padding:"4px 0"}}>Order: {payOid}</div>}
        <Inp l="Amount *" v={form.amt||payAmt||""} oc={e=>setForm({...form,amt:e.target.value})} t="number" p="Amount..."/>
        <Sel l="Method" v={form.method||"Cash"} oc={e=>setForm({...form,method:e.target.value})}
          ch={["Cash","UPI","Cheque","NEFT/RTGS","Bank Transfer","Card"].map(m=><option key={m}>{m}</option>)}/>
        {(accounts||[]).length>0&&<Sel l="Account" v={form.account_id||""} oc={e=>setForm({...form,account_id:e.target.value})}
          ch={[<option key="" value="">-- Account (Optional) --</option>,...(accounts||[]).map(a=><option key={a.id} value={a.id}>{a.type==="cash"?"💵":"🏦"} {a.name}</option>)]}/>}
        <Inp l="Note (Optional)" v={form.note||""} oc={e=>setForm({...form,note:e.target.value})} p="Cheque no, UTR..."/>
        <div style={{display:"flex",gap:8,marginTop:8}}>
          <Btn ch={`✅ Save ₹${fmt(form.amt||payAmt||0)}`} oc={async()=>{
            if(!form.cust){showToast("Please select a customer!","err");return;}
            const amt=+(form.amt||payAmt||0);
            if(!amt||amt<=0){showToast("Please enter an amount!","err");return;}
            await addPay(form.cust,amt,form.method||"Cash",payOid||null,form.account_id||null);
            setModal(null);setForm({});setPayOid("");
            showToast("✅ Payment saved!");
          }} color={C.success} s={{flex:1}}/>
          <Btn ch="Cancel" oc={()=>{setModal(null);setForm({});setPayOid("");}} color={C.sub} out s={{flex:1}}/>
        </div>
      </div>}/>}

            {/* ── INVOICE MODAL ── */}
      {prev&&<InvModal
        order={prev}
        co={gc(prev.cid)}
        isAdmin={isA}
        myco={myCompany}
        onClose={()=>setPrev(null)}
        onSave={async(updated)=>{
          setOrders(orders.map(o=>o.id===updated.id?{...o,...updated}:o));
          try{await API.put("/orders/"+updated.id,{...updated});}catch(e){console.log(e);}
          setPrev(null);
          showToast("✅ Invoice updated!");
        }}
      />}

            {/* ── SUPPLIER PAYMENT MODAL ── */}
      {modal==="suppPay"&&<Modal title="💰 Pay Supplier" oc={()=>{setModal(null);setForm({});}} ch={<div>
        <div style={{marginBottom:10,padding:"10px 12px",background:"#F0FDF4",borderRadius:10,border:"1px solid #BBF7D0"}}>
          <div style={{fontWeight:700,fontSize:14,color:C.success}}>{form.supp}</div>
          {(()=>{
            const s=supps.find(x=>x.name===form.supp);
            const totalPur=purs.filter(p=>p.supp===form.supp).reduce((t,p)=>t+(+p.total||0),0);
            const totalPaid=purs.filter(p=>p.supp===form.supp).reduce((t,p)=>t+(+p.paid||0),0);
            const due=totalPur-totalPaid;
            return<div style={{fontSize:11,color:C.sub,marginTop:2}}>
              Total Purchase: ₹{fmt(totalPur)} | Paid: ₹{fmt(totalPaid)} | <span style={{color:C.danger,fontWeight:700}}>Due: ₹{fmt(due)}</span>
            </div>;
          })()}
        </div>
        <Inp l="Amount *" v={form.amt||""} oc={e=>setForm({...form,amt:e.target.value})} t="number" p="Amount..."/>
        <Sel l="Payment Method" v={form.method||"Cash"} oc={e=>setForm({...form,method:e.target.value})}
          ch={["Cash","UPI","Cheque","NEFT/RTGS","Bank Transfer"].map(m=><option key={m}>{m}</option>)}/>
        {(accounts||[]).length>0&&<Sel l="Account" v={form.account_id||""} oc={e=>setForm({...form,account_id:e.target.value})}
          ch={[<option key="" value="">-- Account (Optional) --</option>,...(accounts||[]).map(a=><option key={a.id} value={a.id}>{a.type==="cash"?"💵":"🏦"} {a.name}</option>)]}/>}
        <Inp l="Reference / Note" v={form.note||""} oc={e=>setForm({...form,note:e.target.value})} p="Cheque no, UTR, Bill ref..."/>
        <Inp l="Date" v={form.date||today()} oc={e=>setForm({...form,date:e.target.value})} t="date"/>
        <div style={{display:"flex",gap:8,marginTop:10}}>
          <Btn ch={`✅ Pay ₹${fmt(form.amt||0)}`} oc={async()=>{
            if(!form.supp){showToast("Supplier is missing!","err");return;}
            const amt=+(form.amt||0);
            if(!amt||amt<=0){showToast("Please enter an amount!","err");return;}
            const payload={
              id:"SPAY"+Date.now(),
              supp:form.supp,
              amount:amt,
              method:form.method||"Cash",
              date:form.date||today(),
              ref:form.note||"",
              cid:form.cid||"",
              account_id:form.account_id||"",
              pur_id:form.pur_id||""
            };
            try{
              await API.createSupplierPayment(payload);
              await loadData(true);
              setModal(null);setForm({});
              showToast("✅ Supplier payment saved!");
            }catch(e){showToast("❌ "+e.message,"err");}
          }} color={C.success} s={{flex:1}}/>
          <Btn ch="Cancel" oc={()=>{setModal(null);setForm({});}} color={C.sub} out s={{flex:1}}/>
        </div>
      </div>}/>}

            {modal==="pwd"&&<Modal title={`🔑 Password Change — ${form.uname}`} oc={()=>setModal(null)} ch={<div>
        <Inp l="New Password" v={form.newpwd||""} oc={e=>setForm({...form,newpwd:e.target.value})} req/>
        <Inp l="Confirm Password" v={form.cfmpwd||""} oc={e=>setForm({...form,cfmpwd:e.target.value})} req/>
        {form.newpwd&&form.cfmpwd&&form.newpwd!==form.cfmpwd&&<div style={{background:"#FEF2F2",borderRadius:7,padding:"6px 10px",color:C.danger,fontSize:12,marginBottom:10}}>❌ Passwords do not match!</div>}
        <div style={{display:"flex",gap:7,flexWrap:"wrap"}}><Btn ch="💾 Save" oc={async()=>{
          if(!form.newpwd||form.newpwd!==form.cfmpwd)return;
          const u=users.find(x=>x.id===form.uid);
          if(!u)return;
          try{
            await API.changeUserPassword(u,form.newpwd);
            setModal(null);setForm({});
            showToast("Password changed successfully!");
          }catch(e){
            console.error("Password change error:",e.message);
            showToast("Failed to change password. Please try again.","err");
          }
        }} s={{flex:1}}/><Btn ch="Cancel" oc={async()=>setModal(null)} color={C.sub} out s={{flex:1}}/></div>
      </div>}/>}

      {showScanner&&modal==="prod"&&<BarcodeScannerModal
        onClose={()=>setShowScanner(false)}
        onScan={(code)=>{
          setShowScanner(false);
          setForm(f=>({...f,barcode:code}));
          showToast("✅ Barcode captured: "+code);
        }}
      />}

    </>
  );
}
